import { useState, useEffect, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Input, Label, Button, Badge } from '@/components/ui'
import { Download, Link2, Loader2, AlertCircle, CheckCircle, Copy, Check, Cookie, Upload, Trash2, FileText } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'

interface DownloadResult {
  url: string
  filename?: string
  title?: string
  thumbnail?: string
  duration?: number
  uploader?: string
  serverFile?: boolean
}

const SUPPORTED_SITES = [
  { name: 'YouTube', color: 'text-red-500', domain: 'youtube.com, youtu.be' },
  { name: 'TikTok', color: 'text-pink-500', domain: 'tiktok.com' },
  { name: 'Facebook', color: 'text-blue-500', domain: 'facebook.com, fb.watch' },
  { name: 'Instagram', color: 'text-purple-500', domain: 'instagram.com' },
  { name: 'Twitter/X', color: 'text-sky-500', domain: 'twitter.com, x.com' },
  { name: 'Reddit', color: 'text-orange-500', domain: 'reddit.com' },
  { name: 'Pinterest', color: 'text-red-600', domain: 'pinterest.com' },
  { name: 'Vimeo', color: 'text-cyan-500', domain: 'vimeo.com' },
  { name: 'SoundCloud', color: 'text-orange-400', domain: 'soundcloud.com' },
  { name: 'Twitch', color: 'text-violet-500', domain: 'twitch.tv' },
  { name: 'Dailymotion', color: 'text-blue-400', domain: 'dailymotion.com' },
  { name: 'Bilibili', color: 'text-blue-300', domain: 'bilibili.com' },
  { name: 'Rumble', color: 'text-green-500', domain: 'rumble.com' },
  { name: 'Kick', color: 'text-green-400', domain: 'kick.com' },
]

export function VideoDownloader() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<DownloadResult | null>(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [hasCookies, setHasCookies] = useState(false)
  const [showCookies, setShowCookies] = useState(false)
  const [cookieText, setCookieText] = useState('')
  const [uploadingCookies, setUploadingCookies] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetch('/api/tools/video-download/cookies/status', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setHasCookies(d.hasCookies))
      .catch(() => {})
  }, [])

  const handleDownload = async () => {
    const trimmed = url.trim()
    if (!trimmed) {
      toast.error('Please enter a URL')
      return
    }

    try {
      new URL(trimmed)
    } catch {
      toast.error('Please enter a valid URL')
      return
    }

    setLoading(true)
    setError('')
    setResult(null)

    try {
      const res = await fetch('/api/tools/video-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: trimmed }),
      })

      const data = await res.json()

      if (!res.ok || data.error) {
        setError(data.error || 'Failed to process video. The URL may not be supported.')
        return
      }

      if (data.url) {
        setResult({
          url: data.url,
          filename: data.filename,
          title: data.title,
          thumbnail: data.thumbnail,
          duration: data.duration,
          uploader: data.uploader,
          serverFile: data.serverFile,
        })
      } else {
        setError('Could not extract download link. Try a different URL.')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const copyLink = () => {
    if (!result?.url) return
    const fullUrl = result.serverFile ? window.location.origin + result.url : result.url
    navigator.clipboard.writeText(fullUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleUploadCookies = async () => {
    const text = cookieText.trim()
    if (!text) {
      toast.error('Please paste cookie data or upload a file')
      return
    }
    setUploadingCookies(true)
    try {
      const res = await fetch('/api/tools/video-download/cookies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ cookies: text }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        toast.error(data.error || 'Failed to upload cookies')
        return
      }
      toast.success(data.message || 'Cookies uploaded!')
      setHasCookies(true)
      setCookieText('')
      setShowCookies(false)
    } catch {
      toast.error('Failed to upload cookies')
    } finally {
      setUploadingCookies(false)
    }
  }

  const handleRemoveCookies = async () => {
    try {
      await fetch('/api/tools/video-download/cookies', {
        method: 'DELETE',
        credentials: 'include',
      })
      setHasCookies(false)
      toast.success('Cookies removed')
    } catch {
      toast.error('Failed to remove cookies')
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setCookieText(reader.result as string)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-red-500/20 to-pink-500/20">
            <Download className="h-6 w-6 text-red-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Video Downloader</h1>
            <p className="text-muted-foreground">Download videos from 1800+ supported websites</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        {/* Main */}
        <div className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Download Video</CardTitle>
                  <CardDescription>Paste any video URL to get a download link</CardDescription>
                </div>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={hasCookies ? 'text-green-500' : 'text-muted-foreground'}
                    onClick={() => setShowCookies(!showCookies)}
                    title={hasCookies ? 'Cookies active - click to manage' : 'Upload cookies for private videos'}
                  >
                    <Cookie className="h-4 w-4 mr-1.5" />
                    {hasCookies ? 'Cookies Active' : 'Add Cookies'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Cookie upload panel (admin only) */}
              {isAdmin && showCookies && (
                <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Cookie className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-medium">Browser Cookies</span>
                    </div>
                    {hasCookies && (
                      <Button variant="ghost" size="sm" className="text-red-400 h-7" onClick={handleRemoveCookies}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Upload cookies to download private, age-restricted, or sign-in required videos.
                    Use a browser extension like "Get cookies.txt LOCALLY" to export cookies in Netscape format.
                  </p>
                  <textarea
                    value={cookieText}
                    onChange={e => setCookieText(e.target.value)}
                    placeholder="Paste cookies.txt content here..."
                    className="w-full h-24 px-3 py-2 text-xs font-mono bg-background border border-border rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.cookie,.cookies"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                      <FileText className="h-3.5 w-3.5 mr-1.5" />
                      Upload File
                    </Button>
                    <Button size="sm" onClick={handleUploadCookies} disabled={uploadingCookies || !cookieText.trim()}>
                      {uploadingCookies ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      ) : (
                        <Upload className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Save Cookies
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Video URL</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={url}
                      onChange={e => setUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="pl-9"
                      onKeyDown={e => { if (e.key === 'Enter') handleDownload() }}
                      disabled={loading}
                    />
                  </div>
                  <Button onClick={handleDownload} disabled={loading || !url.trim()}>
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : (
                      <Download className="h-4 w-4 mr-1.5" />
                    )}
                    {loading ? 'Processing...' : 'Download'}
                  </Button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-500">Download Failed</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{error}</p>
                    {isAdmin && (error.includes('cookies') || error.includes('sign-in') || error.includes('authentication') || error.includes('age-restricted')) && !hasCookies && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-amber-400 p-0 h-auto mt-1"
                        onClick={() => setShowCookies(true)}
                      >
                        Upload cookies to fix this
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Result */}
              {result && (
                <div className="space-y-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <p className="text-sm font-medium text-green-500">Ready to download</p>
                    {result.serverFile && (
                      <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">Server file</Badge>
                    )}
                  </div>

                  {/* Video preview */}
                  <div className="flex gap-3">
                    {result.thumbnail && (
                      <img
                        src={result.thumbnail}
                        alt={result.title || 'Thumbnail'}
                        className="w-32 h-20 object-cover rounded-md border border-border shrink-0"
                      />
                    )}
                    <div className="min-w-0 space-y-1">
                      {result.title && (
                        <p className="text-sm font-medium truncate">{result.title}</p>
                      )}
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {result.uploader && <span>{result.uploader}</span>}
                        {result.duration != null && (
                          <span>
                            {Math.floor(result.duration / 60)}:{String(Math.floor(result.duration % 60)).padStart(2, '0')}
                          </span>
                        )}
                      </div>
                      {result.filename && (
                        <p className="text-xs text-muted-foreground font-mono truncate">{result.filename}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <a
                      href={result.url}
                      download={result.filename || 'video.mp4'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button className="w-full">
                        <Download className="h-4 w-4 mr-1.5" />
                        Download Video
                      </Button>
                    </a>
                    <Button variant="outline" onClick={copyLink}>
                      {copied ? (
                        <Check className="h-4 w-4 text-green-400" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* How to use */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-base">How to use</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                <li>Copy the video URL from the platform (YouTube, TikTok, etc.)</li>
                <li>Paste it into the input field above</li>
                <li>Click "Download" and wait for processing</li>
                <li>Click "Download Video" to save the file</li>
              </ol>
              <div className="mt-4 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-400 font-medium">For private or age-restricted videos:</p>
                <ol className="mt-1 space-y-1 text-xs text-muted-foreground list-decimal list-inside">
                  <li>Install a "cookies.txt" browser extension (e.g. "Get cookies.txt LOCALLY")</li>
                  <li>Visit the video site and log in</li>
                  <li>Export cookies using the extension</li>
                  <li>Upload the cookies.txt file using the "Add Cookies" button above</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Supported Sites */}
        <Card className="glass h-fit">
          <CardHeader>
            <CardTitle className="text-base">Supported Platforms</CardTitle>
            <CardDescription>Works with 1800+ video and media sites</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {SUPPORTED_SITES.map(site => (
                <div
                  key={site.name}
                  className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/20 border border-border/50"
                >
                  <span className={`text-sm font-medium ${site.color}`}>{site.name}</span>
                  <span className="text-xs text-muted-foreground font-mono">{site.domain}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              And 1800+ more sites supported by yt-dlp. Just paste any video link and try it.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
